import React, { useEffect, useState } from 'react';
import { LuTriangleAlert, LuCheck, LuClock } from 'react-icons/lu';
import api from '../../services/api';
import { diaCorto } from './diasRuta';

// ¿Qué día va? (25-oct-2026, calendario de zonas). La app le ofrece a la empresa los próximos días de la zona
// del cliente con lugar, para que se lo diga en la misma llamada. Si es urgente, el día siguiente, diciendo
// cómo queda: entra sin mover a nadie, se arma el día alrededor (y qué pasaría a otro día) o, si ese día
// ya está cerrado y no entra, se lo pide a Dispenser La Tienda.
// consulta: lo que va a /empresa/semana/opciones ({pedidoId} o {sedeId|direccion, motivo, equipos, ventanas}).
const franjaTxt = f => (f ? `de ${Number(f.slice(0, 2))} a ${Number(f.slice(6, 8))}` : '');

function TextoUrgente({ u }) {
    if (!u) return null;
    if (u.modo === 'SIN_UBICAR') return <>No encontramos la dirección en el mapa: revisala para ver cuándo entra.</>;
    if (u.modo === 'ENTRA') return <>Entra el {u.dia} {franjaTxt(u.franja)}, sin mover a nadie.</>;
    if (u.modo === 'PEDIR') return <>El {u.dia} ya está armado y no entra sin mover visitas confirmadas: se lo pedimos a Dispenser La Tienda y te avisamos.</>;
    const otros = (u.desplazados || []).length + (u.desplazadosOtros || 0);
    return (
        <>
            Entra el {u.dia}{u.cambiaZona ? `, aunque es día de ${u.zonaDia}: se arma el día alrededor` : ''}. El horario te lo confirmamos a las {u.horaCierre || '17:30'} del día anterior.
            {otros > 0 && <span className="block mt-1 font-bold">Para que entre, pasa{otros !== 1 ? 'n' : ''} a otro día: {[...(u.desplazados || []), ...(u.desplazadosOtros ? [`${u.desplazadosOtros} de otro cliente`] : [])].join(', ')}.</span>}
        </>
    );
}

export default function ElegirDiaPedido({ consulta, urgente, valor, onElegir, onUrgente }) {
    const [op, setOp] = useState(null);
    const [cargando, setCargando] = useState(false);
    const clave = JSON.stringify({ ...consulta, urgente });
    useEffect(() => {
        let vivo = true;
        setCargando(true);
        api.post('/empresa/semana/opciones', JSON.parse(clave))
            .then(r => { if (vivo) setOp(r.data); })
            .catch(() => { if (vivo) setOp(null); })
            .finally(() => { if (vivo) setCargando(false); });
        return () => { vivo = false; };
    }, [clave]);

    // Urgente: queda elegido el día siguiente
    const u = op?.urgente ? { ...op.urgente, horaCierre: op.horaCierre } : null;
    useEffect(() => {
        if (urgente && u?.fecha && u.modo !== 'SIN_UBICAR' && valor !== u.fecha) onElegir(u.fecha);
    }, [urgente, u?.fecha, u?.modo]); // eslint-disable-line react-hooks/exhaustive-deps

    if (cargando && !op) return <p className="text-caption text-muted px-1">Buscando días…</p>;
    if (!op) return null;

    const boton = (on) => `shrink-0 min-w-[4.5rem] h-16 px-2 rounded-xl inline-flex flex-col items-center justify-center gap-0.5 ${on ? 'bg-ink text-page' : 'bg-chip text-secondary'}`;
    return (
        <div className="space-y-2">
            {urgente && u && (
                <div className={`flex items-start gap-2.5 px-3.5 py-3 rounded-2xl text-caption ${u.modo === 'PEDIR' || u.modo === 'SIN_UBICAR' ? 'bg-panel text-ink' : 'bg-[rgba(201,52,31,0.10)] text-ink'}`}>
                    <LuTriangleAlert size={16} className="shrink-0 mt-0.5 text-brand-red" />
                    <span className="flex-1 min-w-0"><TextoUrgente u={u} /></span>
                </div>
            )}
            {!urgente && op.mananaCerrada && (
                <div className="flex items-start gap-2.5 px-3.5 py-3 rounded-2xl text-caption bg-panel text-ink">
                    <LuClock size={16} className="shrink-0 mt-0.5 text-muted" />
                    <span className="flex-1 min-w-0">
                        El {op.mananaCerrada.dia} ya cerró: los pedidos para el día siguiente se toman hasta las {op.horaCierre || '17:30'}.
                        {onUrgente ? ' Si tiene que ir mañana, marcalo como urgente.' : ''}
                        {onUrgente && (
                            <button type="button" onClick={onUrgente} className="block mt-1.5 font-black text-brand-red underline underline-offset-2">
                                Que vaya el {op.mananaCerrada.dia} (urgente)
                            </button>
                        )}
                    </span>
                </div>
            )}
            {!urgente && (
                <>
                    <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-0.5">
                        {op.dias.map((d, i) => {
                            const c = diaCorto(d.fecha);
                            return (
                                <button key={d.fecha} type="button" onClick={() => onElegir(d.fecha)} aria-pressed={valor === d.fecha} className={boton(valor === d.fecha)}>
                                    <span className="text-caption font-bold uppercase">{c.dia} {c.num}</span>
                                    <span className="text-[11px] font-bold opacity-80">{i === 0 ? 'el primero' : `${d.libres} lugar${d.libres !== 1 ? 'es' : ''}`}</span>
                                </button>
                            );
                        })}
                        <button type="button" onClick={() => onElegir(null)} aria-pressed={!valor} className={boton(!valor)}>
                            <span className="text-caption font-bold leading-tight text-center">Sin día<br />todavía</span>
                        </button>
                    </div>
                    <p className="flex items-start gap-1.5 text-caption text-muted px-1">
                        {valor ? <LuCheck size={14} className="shrink-0 mt-0.5" /> : <LuClock size={14} className="shrink-0 mt-0.5" />}
                        <span>
                            {op.dias.length === 0 ? `No hay días de ${op.zonaNombre} con lugar en las próximas dos semanas: queda sin día y te avisamos.`
                                : valor ? `Reservado. El horario te lo confirmamos a las ${op.horaCierre || '17:30'} del día anterior.`
                                    : `Días de ${op.zonaNombre} con lugar. Si no elegís, queda sin día.`}
                        </span>
                    </p>
                </>
            )}
        </div>
    );
}
